/*
 * IMPORT
 */
import 'mapbox-gl/dist/mapbox-gl.css' // Npm: Mapbox css.
import React from 'react' // Npm: React.
import { createRoot } from 'react-dom/client' // Npm: React dom client.
import { Provider } from 'react-redux' // Npm: Redux provider.
import { PersistGate } from 'redux-persist/integration/react' // Npm: Redux persist gate.
import { ChakraProvider } from '@chakra-ui/react' // Npm: Chakra UI provider.
import { BrowserRouter as Router } from 'react-router-dom' // Npm: React router dom library.
import { extendTheme, ThemeProvider } from '@mui/material' // Npm: Material UI theme provider.
import { StyledEngineProvider } from '@mui/material/styles' // Npm: Material UI styled engine provider.
import * as Sentry from "@sentry/react";
import { browserTracingIntegration, replayIntegration } from '@sentry/react';

/*
 * PACKAGES
 */
import 'iife'
import theme from 'theme'
import Redux, { Persist } from 'store'

/*
 * SIBLINGS
 */
import App from 'index.app.js'
import ErrorComponent from 'index.error.js'

/*
 * STYLES
 */
import 'index.style.css'
import {
  cellStyle,
  footerStyle,
  headersStyle,
  headerStyle,
  rowStyle
} from './index.style'

/*
 * FIXES
 */
if ('production' === process.env.NODE_ENV) {
  console.log = () => { };
  console.warn = () => { };
  console.error = (...args) => {
    if (args[0] instanceof Error) {
      Sentry.captureException(args[0]);
    } else {
      Sentry.captureMessage(String(args[0]));
    }
  };
}

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  integrations: [
    browserTracingIntegration(),
    replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0,
  sendDefaultPii: true,
  environment: process.env.NODE_ENV
});

/*
 * THEME CONFIGURATION
 */
const _theme = extendTheme(theme, {
  shadows: [
    'none',
    '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
    '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
    '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
    '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
    '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
    '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
    '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
    '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
    '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
    '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
    '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
    '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
    '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
    '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
    '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
    '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
    '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)'
  ],
  palette: {
    mode: 'light',
    primary: {
      main: '#ff5722'
    }
  },
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '--DataGrid-t-color-background-base': 'transparent !important',
          border: 'none',
          color: '#2B3674',
          fontWeight: 500,
          fontFamily: 'DM Sans, sans-serif',

          '& .MuiDataGrid-main': {
            border: '1px solid #C5CFE8',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
          },
          '& .MuiDataGrid-overlayWrapperInner': {
            position: 'relative'
          },
          '& .MuiDataGrid-overlay': {
            fontFamily: `'Dm Sans', sans-serif`,
            fontWeight: 600
          },
          '& .MuiDataGrid-paper': {
            backgroundColor: '#F4F7FC !important',
            borderRadius: '12px',
            '& *': {
              fontFamily: `'Dm Sans', sans-serif`,
              fontWeight: 500
            }
          },
          '& .MuiDataGrid-cell': {
            display: 'block !important',
            alignItems: 'center',              // vertical center
            justifyContent: 'center',          // horizontal center
            textAlign: 'center',
            overflow: 'visible !important',
            whiteSpace: 'nowrap'
          },
          '& .MuiDataGrid-cell--textCenter': {
            justifyContent: 'flex-start !important'  // Override MUI's default centering behavior
          },
          '& .MuiDataGrid-cellContent': {
            width: '100%',  // Full width for cell content
            textAlign: 'center'  // Center align the content
          },

          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },

          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-virtualScroller': {
            marginTop: '0px !important',
            overflow: 'auto'
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none'
          },
          '& .MuiDataGrid-iconButtonContainer': {
            display: 'none'
          },
          '& .MuiDataGrid-sortIcon': {
            display: 'none'
          },
          "& .MuiDataGrid-overlayWrapper": {
            position: "static !important"
          }
        },
        columnHeaders: headersStyle,
        columnHeader: headerStyle,
        row: rowStyle,
        cell: cellStyle,
        footerContainer: footerStyle
      }
    },

    MuiChip: {
      styleOverrides: {
        label: {
          fontSize: '15px'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'white !important',
          boxShadow: '0 0 16px rgba(0, 0, 0, 0.1)'
        },
        rounded: {
          borderRadius: '12px'
        }
      }
    },

    MuiAutocomplete: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--chakra-colors-secondaryGray-300)',
          borderRadius: '12px'
        },
        inputRoot: {
          '& input::placeholder': {
            color: 'var(--chakra-colors-secondaryGray-600)',
            opacity: 1
          }
        },
        '& .MuiInputBase-root.MuiOutlinedInput-root': {
          padding: '0 !important'
        }
      }
    },

    // Menu dropdown
    MuiMenu: {
      styleOverrides: {
        paper: {
          maxHeight: '400px',
          marginTop: '-20px',
          backgroundColor: 'white'
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: 'DM Sans, sans-serif !important',
          color: 'var(--chakra-colors-gray-500)',
          minHeight: '40px',
          gap: '12px',
          paddingLeft: '20px',
          paddingRight: '20px'
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          margin: 0,
          flex: 'none'
        },
        primary: {
          fontFamily: 'DM Sans, sans-serif !important',
          color: 'var(--chakra-colors-gray-500)',
          fontSize: 'inherit',
          fontWeight: 'inherit'
        }
      }
    },

    MuiCheckbox: {
      styleOverrides: {
        colorPrimary: {
          color: '#f7797d !important'
        }
      }
    }
  }
})

/*
 * RENDER
 */
createRoot(document.getElementById('root')).render(
  <Sentry.ErrorBoundary fallback={<ErrorComponent />} showDialog>
    <Provider store={Redux}>
      <PersistGate persistor={Persist}>
        <ThemeProvider theme={_theme}>
          <StyledEngineProvider injectFirst>
            <ChakraProvider theme={_theme}>
              <Router>
                <App isAnimating />
              </Router>
            </ChakraProvider>
          </StyledEngineProvider>
        </ThemeProvider>
      </PersistGate>
    </Provider>
  </Sentry.ErrorBoundary>
)
